// /src/components/chat/ChatMain.js
import React, { useImperativeHandle, forwardRef, useState, useRef, useEffect } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  IconButton,
  Tooltip,
  Snackbar,
  Switch
} from '@mui/material';
import BotCustomizerPanel from './BotCustomizerPanel';
import ChatWidget from './ChatWidget';
import IdleDetectionManager from './IdleDetectionManager.js';
import DragHandle from './shared/DragHandle';
import SystemAlert from '../../common/SystemAlert.js';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import { WS_URL, API_URL } from '../../common/Constants';
import Cookies from '../../common/Cookies.js';
import { useLocation } from 'react-router-dom';
import { trackChatActivity } from '../../services/chatSessionService';

const LOCAL_STORAGE_KEY = 'chatCustomization';

// Move defaultCustomization above the component so it is not redeclared
const defaultCustomization = {
  botTitle: 'Chat Bot',
  botMessageColor: '#E3F2FD',
  userMessageColor: '#F5F5F5',
  chatBackgroundColor: '#F8F9FA',
  headerBackgroundColor: '#FFFFFF',
  footerBackgroundColor: '#FFFFFF',
  inputPlaceholder: 'Type your message...',
  inputBoxColor: '#FFFFFF',
  sendIconColor: '#1976d2',
  sendIconDisabledColor: 'rgba(0, 0, 0, 0.26)',
  chatControlIconColor: '#555555',
  showTimestamps: true,
  timestampFormat: 'MM/DD/YYYY hh:mm a',
  timestampColor: '#888888',
  agentNameColor: '#000000',
  botTitleColor: '#000000',
  logoShape: 'round',
  chatIconShape: 'round',
  chatIconSurface: 'solid',
  waitingMessage: 'Thinking...',
  chatHeight: 600,
  chatWidth: 400,
  fontSize: 'medium',
  idleDetectionEnabled: true,
  idleTimeout: 60,
  idleMessage: "It looks like you're idle. Can I help you with anything else?",
  closeIdleTimeout: 300,
  closeIdleMessage: "Thank you for chatting with us, please reconnect at anytime.",
  presetResponses: [
    { id: '1', text: "What services do you offer?", active: true },
    { id: '2', text: "How can I contact support?", active: true },
  ],
  soundEnabled: true,
  isMuted: false,
  model: '1',
  visitorTranscriptEnabled: true,
  //  tenantId: Cookies.getTenantId(),
  onPageLoadChatState: 'open', // New default property
  intermediate: {
    enabled: false,
    width: 400,
    height: 600,
    mediaUrl: '',
    html: '',
    buttonTitle: 'Start Conversation',
  },
};

function normalizeDbCustomization(dbCustomization) {
  const normalized = { ...dbCustomization };
  if (typeof normalized.visitorTranscriptEnabled !== 'boolean') {
    normalized.visitorTranscriptEnabled = false;
  }
  if (typeof normalized.departmentEmailNotificationsEnabled !== 'boolean') {
    normalized.departmentEmailNotificationsEnabled = false;
  }
  return normalized;
}

// Utility to get initial chat closed state, honoring customization and localStorage
function getInitialChatClosed(customization) {
  const persisted = localStorage.getItem('isChatClosed');
  if (persisted !== null) return persisted === 'true';
  return customization.onPageLoadChatState === 'closed';
}


// Simple deep equality check for objects/arrays
function deepEqual(a, b) {
  if (a === b) return true;
  if (typeof a !== typeof b) return false;
  if (typeof a !== 'object' || a === null || b === null) return false;
  if (Array.isArray(a)) {
    if (!Array.isArray(b) || a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (!deepEqual(a[i], b[i])) return false;
    }
    return true;
  }
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  for (let key of aKeys) {
    if (!deepEqual(a[key], b[key])) return false;
  }
  return true;
}

const CALCOM_HISTORY_SCHEDULING_HTML = '<p><strong>Scheduling</strong></p><p>Choose a meeting type, then pick a date range to load available times.</p>';
const CALCOM_HISTORY_SLOTS_HTML = '<p><strong>Available times</strong></p><p>Choose a time, then enter your details to book.</p>';
const CALCOM_HISTORY_NO_SLOTS_HTML = '<p>No open times in this range. Try another date range or meeting length.</p>';

/** Unwrap legacy bot rows and replace LLM fluff with the same short copy as live Cal.com widget turns. */
function normalizeCalcomHistoryMessage(msg) {
  if (!msg) return msg;
  let m = typeof msg === 'object' ? { ...msg } : msg;
  if (typeof m.text === 'string') {
    const t = m.text.trim();
    if (t.startsWith('{') && !m.calcomEventTypes?.length && !m.calcomSlots && !m.quickReplies?.length) {
      try {
        const o = JSON.parse(t);
        if (typeof o.text === 'string') {
          m = { ...m, text: o.text };
          if (Array.isArray(o.calcomEventTypes) && o.calcomEventTypes.length > 0) {
            m.calcomEventTypes = o.calcomEventTypes;
          }
          if (o.calcomSlots && typeof o.calcomSlots === 'object') {
            m.calcomSlots = o.calcomSlots;
          }
          if (Array.isArray(o.quickReplies) && o.quickReplies.length > 0) {
            m.quickReplies = o.quickReplies;
          }
        }
      } catch {
        /* keep m */
      }
    }
  }
  if (m.calcomEventTypes?.length && !m.calcomSlots) {
    return { ...m, text: CALCOM_HISTORY_SCHEDULING_HTML };
  }
  if (m.calcomSlots && typeof m.calcomSlots === 'object') {
    const at = m.calcomSlots.allTimes;
    const noSlots = Array.isArray(at) && at.length === 0;
    return { ...m, text: noSlots ? CALCOM_HISTORY_NO_SLOTS_HTML : CALCOM_HISTORY_SLOTS_HTML };
  }
  return m;
}


const ChatMain = forwardRef(({
  // ...existing code...
  agentId, customizationFromDb, defaultAgentName, selectedInstructionId,
  setSelectedInstructionId, onCustomizationSaved, hasUnsavedChanges, setHasUnsavedChanges, ...rest
}, ref) => {
  // Reset customization to last saved
  const resetCustomization = () => {
    setCustomization(lastSavedCustomization);
    setHasUnsavedChangesToUse(false);
  };


  // All state, refs, and effects are declared only once below
  const [customization, setCustomization] = useState(() => {
    let dbCustomization = customizationFromDb;
    if (!dbCustomization) {
      return defaultAgentName
        ? { ...defaultCustomization, agentName: defaultAgentName }
        : { ...defaultCustomization };
    }
    if (typeof dbCustomization === 'string') {
      try {
        dbCustomization = JSON.parse(dbCustomization);
      } catch (e) {
        dbCustomization = undefined;
      }
    }
    if (!dbCustomization) {
      return defaultAgentName
        ? { ...defaultCustomization, agentName: defaultAgentName }
        : { ...defaultCustomization };
    }
    const normalizedDbCustomization = normalizeDbCustomization(dbCustomization);
    if (defaultAgentName && (!dbCustomization.botTitle || dbCustomization.botTitle === 'Chat Bot')) {
      return { ...defaultCustomization, ...normalizedDbCustomization, agentName: defaultAgentName };
    }
    return { ...defaultCustomization, ...normalizedDbCustomization };
  });
  // Unsaved changes tracking
  // Use props if provided, otherwise use local state
  const [localLastSavedCustomization, setLocalLastSavedCustomization] = useState(customizationFromDb || defaultCustomization);
  const lastSavedCustomization = typeof rest.lastSavedCustomization !== 'undefined' ? rest.lastSavedCustomization : localLastSavedCustomization;
  const setLastSavedCustomization = typeof rest.setLastSavedCustomization === 'function' ? rest.setLastSavedCustomization : setLocalLastSavedCustomization;
  // Use props if provided, otherwise use local state
  const [localHasUnsavedChanges, setLocalHasUnsavedChanges] = useState(false);
  const hasUnsavedChangesToUse = typeof hasUnsavedChanges === 'boolean' ? hasUnsavedChanges : localHasUnsavedChanges;
  const setHasUnsavedChangesToUse = typeof setHasUnsavedChanges === 'function' ? setHasUnsavedChanges : setLocalHasUnsavedChanges;

  // (Removed duplicate resetCustomization declaration)
  // State for session end offer prompt (now just tracks text/options for close confirm)
  const [closeConfirmText, setCloseConfirmText] = useState(null); // string or null
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const newAgent = params.get('newAgent') === 'true';

  const [messages, setMessages] = useState([]);
  const [calcomWidget, setCalcomWidget] = useState({ open: false, username: '', eventSlug: '' });
  // Track if initial greeting/starter prompts have been sent (lifted from ChatWidget)
  const [initialMessageSent, setInitialMessageSent] = useState(false);
  // Used to force ChatWidget to remount and re-trigger greeting/starter logic
  const [chatWidgetKey, setChatWidgetKey] = useState(0);
  const [isChatClosed, setIsChatClosed] = useState(() => getInitialChatClosed(customization));
  const [userToggledChat, setUserToggledChat] = useState(false);

  // Persist open/closed state on change
  useEffect(() => {
    localStorage.setItem('isChatClosed', isChatClosed);
  }, [isChatClosed]);

  // Reset userToggledChat when agentId or customization changes (new agent loaded)
  useEffect(() => {
    setUserToggledChat(false);
  }, [agentId, customization.onPageLoadChatState]);

  // Sync isChatClosed with customization.onPageLoadChatState if user hasn't toggled
  useEffect(() => {
    if (!userToggledChat) {
      setIsChatClosed(customization.onPageLoadChatState === 'closed');
    }
  }, [customization.onPageLoadChatState, agentId, userToggledChat]);
  const [sidebarWidth, setSidebarWidth] = useState(300);
  const [isConnected, setIsConnected] = useState(false);
  const [ws, setWs] = useState(null);
  const wsRef = useRef(null);
  const intentionallyClosedRef = useRef(false); // Track intentional WebSocket closures
  const currentBotMessageRef = useRef(null);
  const streamAccumulatorRef = useRef(''); // Accumulator for FULL message content (source of truth)
  const pendingQuickRepliesRef = useRef(null);
  const streamFlushIntervalRef = useRef(null); // Interval for periodic UI updates
  const lastFlushedLengthRef = useRef(0); // Track what we've already flushed to UI
  const soundEnabledRef = useRef(true);
  const isMutedRef = useRef(false);
  const [isMuted, setIsMuted] = useState(false);
  const [devicePreview, setDevicePreview] = useState('desktop');
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [isInstructionsModalOpen, setIsInstructionsModalOpen] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [tempChatWidth, setTempChatWidth] = useState('400');
  const [tempChatHeight, setTempChatHeight] = useState('600');
  const [alert, setAlert] = useState(null);

  // Session management state
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false); // Track if waiting for bot response



  useEffect(() => {
    // Compare customization to lastSavedCustomization
    if (!deepEqual(customization, lastSavedCustomization)) {
      setHasUnsavedChangesToUse(true);
    } else {
      setHasUnsavedChangesToUse(false);
    }
  }, [customization, lastSavedCustomization]);

  useEffect(() => {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(customization));
    // Update refs when customization changes
    soundEnabledRef.current = customization.soundEnabled;
    isMutedRef.current = customization.isMuted;
    setIsMuted(customization.isMuted);
    setTempChatWidth(customization.chatWidth.toString());
    setTempChatHeight(customization.chatHeight.toString());
  }, [customization]);

  // Add this effect to sync customization when customizationFromDb changes
  useEffect(() => {
    let dbCustomization = customizationFromDb;
    if (typeof dbCustomization === 'string') {
      try {
        dbCustomization = JSON.parse(dbCustomization);
      } catch (e) {
        dbCustomization = undefined;
      }
    }
    if (dbCustomization) {
      const normalizedDbCustomization = normalizeDbCustomization(dbCustomization);
      setLastSavedCustomization(normalizedDbCustomization);
      setCustomization(prev => ({ ...prev, ...normalizedDbCustomization }));
      setHasUnsavedChangesToUse(false);
    }
  }, [customizationFromDb]);
  useEffect(() => {
    if (customizationFromDb) {
      let dbCustomization = customizationFromDb;
      if (typeof dbCustomization === 'string') {
        try {
          dbCustomization = JSON.parse(dbCustomization);
        } catch (e) {
          dbCustomization = undefined;
        }
      }
      if (dbCustomization) {
        const normalizedDbCustomization = normalizeDbCustomization(dbCustomization);
        setCustomization(prev => ({
          ...prev,
          ...normalizedDbCustomization,
        }));
      }
    }
  }, [customizationFromDb]);

  // WebSocket connection with session management
  useEffect(() => {
    // Don't connect if chat is closed
    if (isChatClosed) {
      console.log('Chat is closed - skipping WebSocket connection');
      return;
    }

    let websocket = null;
    let reconnectAttempt = 0;
    const maxReconnectAttempts = 5;
    const reconnectDelay = 3000; // 3 seconds

    const connectWebSocket = async () => {
      // Create WebSocket connection for internal/customizer sessions using the public endpoint
      const WS_BASE = WS_URL.replace('http:', 'ws:').replace('https:', 'wss:');
      const tenantId = Cookies.getTenantId();

      console.log('🔍 DEBUG WebSocket connection values - agentId:', agentId, 'tenantId:', tenantId);

      // Use the public WebSocket endpoint with internal flag (always create new session)
      const wsUrl = `${WS_BASE}/ws/public-chat/${encodeURIComponent(agentId)}/${encodeURIComponent(tenantId)}/1`;
      console.log('🔌 Creating new internal WebSocket session:', wsUrl);

      websocket = new WebSocket(wsUrl);

      websocket.onopen = async () => {
        console.log('Connected to WebSocket (ChatMain - Internal Session)');
        setIsConnected(true);
        reconnectAttempt = 0; // Reset reconnect attempts on successful connection
        intentionallyClosedRef.current = false; // Reset the flag on successful connection

        // Load history from previous session if available (like working widget)
        const lastSessionId = localStorage.getItem('chatMainLastSessionId');
        if (lastSessionId && messages.length === 0) {
          console.log('📜 Loading chat history from previous session:', lastSessionId);
          try {
            const response = await fetch(`${API_URL}/public/chat/history/${lastSessionId}`);
            if (response.ok) {
              const historyData = await response.json();
              if (historyData && historyData.length > 0) {
                console.log('📜 Successfully loaded chat history:', historyData.length, 'messages');
                setMessages(historyData.map(normalizeCalcomHistoryMessage));
              } else {
                console.log('📜 No chat history found for previous session');
              }
            } else {
              console.log('📜 Previous session expired or no history available');
            }
          } catch (error) {
            console.error('Error loading chat history:', error);
          }
        } else if (lastSessionId) {
          console.log('📜 Previous session exists but messages already loaded in React state');
        } else {
          console.log('📜 No previous session found, starting fresh');
        }
      };

      websocket.onclose = () => {
        console.log('Disconnected from WebSocket ChatMain.js----');
        setIsConnected(false);

        // Only attempt to reconnect if the closure was NOT intentional
        if (!intentionallyClosedRef.current && reconnectAttempt < maxReconnectAttempts) {
          reconnectAttempt++;
          console.log(`Attempting to reconnect (${reconnectAttempt}/${maxReconnectAttempts})...`);
          setTimeout(connectWebSocket, reconnectDelay);
        } else if (intentionallyClosedRef.current) {
          console.log('WebSocket was intentionally closed - no reconnection needed');
        } else {
          console.log('Reconnection attempts exhausted');
        }
      };

      websocket.onmessage = (event) => {

        console.log('WebSocket message received:', event.data);

        try {
          const response = JSON.parse(event.data);
          // Handle session-end-offer message type
          if (response.type === 'session-end-offer') {
            setCloseConfirmText(response.content || 'I want to make sure I\'ve helped with everything you needed. Are you ready to finish, or should we continue?');
            setShowCloseConfirm(true);
            setLoading(false);
            return;
          }

          if (response.type === 'calcom_widget') {
            const u = response.username || '';
            const s = response.eventSlug || '';
            if (u && s) {
              setCalcomWidget({ open: true, username: u, eventSlug: s });
            }
            setLoading(false);
            return;
          }

          if (response.type === 'calcom_event_types') {
            setMessages((prev) => [
              ...prev,
              {
                isBot: true,
                timestamp: new Date(),
                text: '<p><strong>Scheduling</strong></p><p>Choose a meeting type, then pick a date range to load available times.</p>',
                calcomEventTypes: Array.isArray(response.items) ? response.items : [],
              },
            ]);
            setLoading(false);
            return;
          }

          if (response.type === 'calcom_slots') {
            const payload = response.payload && typeof response.payload === 'object' ? response.payload : {};
            const slotsMsg = {
              isBot: true,
              timestamp: new Date(),
              text: '<p><strong>Available times</strong></p><p>Choose a time, then enter your details to book.</p>',
              calcomSlots: payload,
            };
            setMessages((prev) => {
              const list = Array.isArray(prev) ? prev : [];
              const filtered = list.filter((m) => !m.isWaiting);

              const last = filtered[filtered.length - 1];
              if (last && last.isBot && !last.calcomSlots && !last.calcomEventTypes?.length) {
                const t = (last.text || '').toLowerCase();
                if (t.includes('available times') || t.includes('scheduling')) {
                  return [...filtered.slice(0, -1), { ...last, ...slotsMsg }];
                }
              }

              let base = filtered;
              const u = base.length - 1;
              if (u >= 1 && base[u] && base[u].isBot !== true) {
                const next = [...base];
                const prevBot = next[u - 1];
                if (
                  prevBot &&
                  prevBot.isBot === true &&
                  prevBot.calcomEventTypes?.length &&
                  !prevBot.calcomSlots
                ) {
                  next[u - 1] = {
                    ...prevBot,
                    calcomEventTypes: undefined,
                    text: '<p><strong>Scheduling</strong></p><p>Choose a meeting type, then pick a date range to load available times.</p>',
                  };
                  base = next;
                }
              }

              return [...base, slotsMsg];
            });
            setLoading(false);
            return;
          }

          if (response.type === 'quick_replies') {
            const qrItems = Array.isArray(response.items) ? response.items : [];
            if (qrItems.length > 0) {
              const streaming = currentBotMessageRef.current !== null;
              if (streaming) {
                pendingQuickRepliesRef.current = qrItems;
              } else {
                setMessages((prev) => {
                  const list = Array.isArray(prev) ? prev : [];
                  const filtered = list.filter((m) => !m.isWaiting);
                  const withoutOrphans = filtered.filter(m =>
                    !(m.isBot && !(m.text || '').trim() && m.quickReplies?.length)
                  );
                  const last = withoutOrphans[withoutOrphans.length - 1];
                  if (last && last.isBot) {
                    return [...withoutOrphans.slice(0, -1), { ...last, quickReplies: qrItems }];
                  }
                  pendingQuickRepliesRef.current = qrItems;
                  return withoutOrphans;
                });
              }
            }
            setLoading(false);
            return;
          }

          // Only track chat activity for non-heartbeat messages
          if (response.type !== 'heartbeat') {
            console.log('🔄 ChatMain: Tracking chat activity from WebSocket message');
            trackChatActivity();
          } else {
            console.log('🔄 ChatMain: Skipping activity tracking for heartbeat message');
          }

          // Handle session establishment 
          if (response.type === 'session' && response.sessionId) {
            console.log('Received sessionId from WebSocket (Internal):', response.sessionId);
            setSessionId(response.sessionId);
            // Don't sync with chatSessionService - keep sessions independent
            console.log('✅ Internal session established independently');
          }

          if (response.type === 'stream') {
            // ALWAYS accumulate content immediately (source of truth)
            streamAccumulatorRef.current = (streamAccumulatorRef.current || '') + (response.content || '');

            // Check if this is the first chunk - need to create the message
            if (currentBotMessageRef.current === null) {
              // Mark that we're starting a stream
              currentBotMessageRef.current = -1;
              lastFlushedLengthRef.current = 0;
              pendingQuickRepliesRef.current = null;

              // Re-enable send button when streaming starts
              setLoading(false);

              // Play received sound if enabled
              if (soundEnabledRef.current && !isMutedRef.current) {
                const audio = new Audio('/sounds/rec_default.wav');
                audio.volume = 0.5;
                audio.play().catch(err => console.error('Error playing sound:', err));
              }

              // Create the message (or convert waiting message)
              setMessages(prev => {
                const waitingIndex = prev.findIndex(msg => msg.isWaiting === true);
                let newMessages;

                if (waitingIndex !== -1) {
                  currentBotMessageRef.current = waitingIndex;
                  newMessages = [...prev];
                  newMessages[waitingIndex] = {
                    ...newMessages[waitingIndex],
                    text: streamAccumulatorRef.current,
                    isWaiting: false
                  };
                } else {
                  currentBotMessageRef.current = prev.length;
                  newMessages = [...prev, {
                    text: streamAccumulatorRef.current,
                    isBot: true,
                    timestamp: new Date(),
                    isWaiting: false
                  }];
                }
                lastFlushedLengthRef.current = streamAccumulatorRef.current.length;
                return newMessages;
              });

              // Start periodic flush interval (every 100ms)
              if (!streamFlushIntervalRef.current) {
                streamFlushIntervalRef.current = setInterval(() => {
                  const index = currentBotMessageRef.current;
                  const accumulated = streamAccumulatorRef.current || '';
                  const lastFlushed = lastFlushedLengthRef.current;

                  // Only update if there's new content
                  if (index !== null && index !== -1 && accumulated.length > lastFlushed) {
                    setMessages(prev => {
                      if (index >= 0 && index < prev.length && prev[index]) {
                        const newMessages = [...prev];
                        newMessages[index] = {
                          ...newMessages[index],
                          text: accumulated
                        };
                        lastFlushedLengthRef.current = accumulated.length;
                        return newMessages;
                      }
                      return prev;
                    });
                  }
                }, 100);
              }
            }
            // For subsequent chunks, content is already accumulated - flush interval handles UI
          } else if (response.type === 'stream-end') {
            // Stop the flush interval
            if (streamFlushIntervalRef.current) {
              clearInterval(streamFlushIntervalRef.current);
              streamFlushIntervalRef.current = null;
            }

            // Final flush - use accumulator as source of truth
            const finalContent = streamAccumulatorRef.current || '';
            const index = currentBotMessageRef.current;
            const qrItems = pendingQuickRepliesRef.current;
            pendingQuickRepliesRef.current = null;
            const quickReplyFields = Array.isArray(qrItems) && qrItems.length > 0
              ? { quickReplies: qrItems }
              : {};

            if (finalContent) {
              if (index === -1) {
                // Edge case: stream-end arrived before first setMessages callback completed
                // The message hasn't been created yet, so create it now with final content
                setMessages(prev => {
                  const cleaned = prev.filter(m =>
                    !(m.isBot && !(m.text || '').trim() && m.quickReplies?.length)
                  );
                  const waitingIndex = cleaned.findIndex(msg => msg.isWaiting === true);
                  let newMessages;

                  if (waitingIndex !== -1) {
                    newMessages = [...cleaned];
                    newMessages[waitingIndex] = {
                      ...newMessages[waitingIndex],
                      text: finalContent,
                      isWaiting: false,
                      ...quickReplyFields,
                    };
                  } else {
                    // Check if the message was already added by the first callback
                    // (in case callbacks ran out of order)
                    const lastMsg = cleaned[cleaned.length - 1];
                    if (lastMsg && lastMsg.isBot && !lastMsg.text) {
                      // Empty bot message exists - update it
                      newMessages = [...cleaned];
                      newMessages[cleaned.length - 1] = {
                        ...lastMsg,
                        text: finalContent,
                        ...quickReplyFields,
                      };
                    } else if (lastMsg && lastMsg.isBot && lastMsg.text === finalContent) {
                      if (quickReplyFields.quickReplies && !lastMsg.quickReplies?.length) {
                        newMessages = [...cleaned];
                        newMessages[cleaned.length - 1] = { ...lastMsg, ...quickReplyFields };
                        return newMessages;
                      }
                      return cleaned;
                    } else {
                      // Create new message
                      newMessages = [...cleaned, {
                        text: finalContent,
                        isBot: true,
                        timestamp: new Date(),
                        isWaiting: false,
                        ...quickReplyFields,
                      }];
                    }
                  }
                  return newMessages;
                });
              } else if (index !== null && index >= 0) {
                // Normal case: update the existing message
                setMessages(prev => {
                  const withoutOrphans = prev.filter(m =>
                    !(m.isBot && !(m.text || '').trim() && m.quickReplies?.length)
                  );
                  if (index < prev.length && prev[index]) {
                    const target = prev[index];
                    const newIdx = withoutOrphans.indexOf(target);
                    if (newIdx >= 0) {
                      const newMessages = [...withoutOrphans];
                      newMessages[newIdx] = {
                        ...newMessages[newIdx],
                        text: finalContent,
                        ...quickReplyFields,
                      };
                      return newMessages;
                    }
                  }
                  return withoutOrphans;
                });
              }
            }

            // Reset all streaming state
            currentBotMessageRef.current = null;
            streamAccumulatorRef.current = '';
            lastFlushedLengthRef.current = 0;
            setLoading(false);
          } else if (response.type === 'end' || response.type === 'error') {
            // Stop the flush interval
            if (streamFlushIntervalRef.current) {
              clearInterval(streamFlushIntervalRef.current);
              streamFlushIntervalRef.current = null;
            }
            // Reset streaming state
            currentBotMessageRef.current = null;
            streamAccumulatorRef.current = '';
            lastFlushedLengthRef.current = 0;
            setLoading(false);
          } else if (response.type === 'completion') {
            // Stop the flush interval
            if (streamFlushIntervalRef.current) {
              clearInterval(streamFlushIntervalRef.current);
              streamFlushIntervalRef.current = null;
            }
            // Reset streaming state
            currentBotMessageRef.current = null;
            streamAccumulatorRef.current = '';
            lastFlushedLengthRef.current = 0;
            setLoading(false);
          } else if (response.type === 'form') {
            // Re-enable send button when form is received
            setLoading(false);
            console.log('Dynamic formFields:', response.formFields);
            let fieldsArray = [];
            if (Array.isArray(response.formFields)) {
              fieldsArray = response.formFields;
            } else if (typeof response.formFields === 'string') {
              try {
                fieldsArray = JSON.parse(response.formFields);
              } catch (e) {
                console.error('Failed to parse formFields JSON:', e);
                fieldsArray = [];
              }
            }

            setMessages(prev => {
              // Remove any waiting/thinking message
              const filtered = prev.filter(msg => !msg.isWaiting);

              // Prepare dynamic fields for the form
              const dynamicFields = fieldsArray.map(f => ({
                name: f.fieldName,
                label: f.fieldName,
                type: f.fieldType === 'String' ? 'text'
                  : f.fieldType === 'Number' ? 'number'
                    : f.fieldType === 'Email' ? 'email'
                      : f.fieldType === 'Text' ? 'textarea'
                        : 'text',
                required: !!f.requiredStatus,
                multiline: f.fieldType === 'Text',
                rows: f.fieldType === 'Text' ? 3 : undefined
              }));

              // Prepare new messages array
              let newMessages = [...filtered];

              // Add requestMessage if present and not blank/null
              if (response.requestMessage && response.requestMessage.trim() !== '') {
                newMessages.push({
                  text: response.requestMessage,
                  isBot: true,
                  timestamp: new Date()
                });
              }
              console.log('Dynamic form message:', response.successMessage);
              // Add the dynamic form message
              newMessages.push({
                id: Date.now(),
                isForm: true,
                formFields: dynamicFields,
                isBot: true,
                timestamp: new Date(),
                // Pass successMessage for later use
                successMessage: response.successMessage
              });

              return newMessages;
            });
            return; // Don't process further for this message
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      wsRef.current = websocket;
      setWs(websocket);
    };

    connectWebSocket();

    return () => {
      if (websocket) {
        intentionallyClosedRef.current = true; // Mark as intentional before closing
        websocket.close();
      }
      // Stop flush interval and reset refs when unmounting
      if (streamFlushIntervalRef.current) {
        clearInterval(streamFlushIntervalRef.current);
        streamFlushIntervalRef.current = null;
      }
      currentBotMessageRef.current = null;
      streamAccumulatorRef.current = '';
      lastFlushedLengthRef.current = 0;
    };
  }, [agentId, isChatClosed]); // <-- Also depend on isChatClosed state

  const handleIdleMessage = () => {

    console.log('User is idle, showing idle message');
    const timestamp = new Date();
    const botMessage = {
      text: customization.idleMessage,
      isBot: true,
      timestamp: timestamp
    };
    setMessages(prev => {
      const newMessages = [...prev, botMessage];
      return newMessages;
    });
  };

  const handleIdleClose = () => {
    console.log('User is still idle, closing chat');
    const timestamp = new Date();
    const closeMessage = {
      text: customization.closeIdleMessage,
      isBot: true,
      timestamp: timestamp
    };
    setMessages(prev => {
      const newMessages = [...prev, closeMessage];
      return newMessages;
    });

    // Close the chat after a short delay to ensure the message is visible
    setTimeout(() => {
      console.log('Actually closing the chat now');
      setIsChatClosed(true);
      // End the chat session when closing due to inactivity
      endSession('timeout');
    }, 1000);
  };

  const handleCopyCode = () => {
    navigator.clipboard.writeText(generateExportCode())
      .then(() => {
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 3000);
      })
      .catch(err => console.error('Failed to copy: ', err));
  };

  // Function to generate export code
  const generateExportCode = () => {
    const logoCode = customization.logo ?
      `<img src="${customization.logo}" alt="${customization.botTitle}" style="max-width:50px;height:auto;">` : '';

    return `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${customization.botTitle || 'Chat Bot'} Widget</title>
    <style>
        .chat-widget {
            display: flex;
            flex-direction: column;
            height: ${customization.chatHeight}px;
            width: ${customization.chatWidth}px;
            max-width: 100%;
            max-height: 100vh;
            border-radius: 12px;
            background-color: white;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
            font-family: Arial, sans-serif;
        }
        .chat-header {
            padding: 16px;
            border-bottom: 1px solid #E8E8E8;
            display: flex;
            align-items: center;
            gap: 12px;
            background-color: ${customization.headerBackgroundColor || '#FFFFFF'};
        }
        .chat-messages {
            flex-grow: 1;
            overflow-y: auto;
            display: flex;
            flex-direction: column;
            padding: 16px;
            background-color: ${customization.chatBackgroundColor || '#F8F9FA'};
        }
        .message {
            padding: 12px;
            margin: 8px 0;
            max-width: 70%;
            border-radius: 12px;
        }
        .bot-message {
            align-self: flex-start;
            background-color: ${customization.botMessageColor || '#E3F2FD'};
        }
        .user-message {
            align-self: flex-end;
            background-color: ${customization.userMessageColor || '#F5F5F5'};
        }
        .chat-input {
            display: flex;
            padding: 16px;
            border-top: 1px solid #E8E8E8;
            background-color: ${customization.footerBackgroundColor || '#FFFFFF'};
        }
        .chat-input input {
            flex-grow: 1;
            padding: 12px;
            border: 1px solid #E8E8E8;
            border-radius: 4px;
            margin-right: 8px;
            background-color: ${customization.inputBoxColor || '#FFFFFF'};
        }
        .chat-input button {
            padding: 12px 16px;
            background-color: ${customization.sendIconColor || '#1976d2'};
            color: white;
            border: none;
            border-radius: 4px;
            cursor: pointer;
        }
        .chat-input button:disabled {
            background-color: ${customization.sendIconDisabledColor || '#cccccc'};
        }
        .preset-responses {
            display: flex;
            flex-wrap: wrap;
            gap: 8px;
            margin-bottom: 8px;
        }
        .preset-button {
            padding: 8px 12px;
            background-color: #f1f1f1;
            border: 1px solid #ddd;
            border-radius: 16px;
            cursor: pointer;
            font-size: 14px;
            white-space: nowrap;
        }
        .chat-icon {
            position: fixed;
            bottom: 20px;
            right: 20px;
            width: 60px;
            height: 60px;
            border-radius: 50%;
            background-color: ${customization.headerBackgroundColor || '#FFFFFF'};
            display: flex;
            justify-content: center;
            align-items: center;
            cursor: pointer;
            box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
            z-index: 1000;
        }
        .chat-disclaimer {
            font-size: 12px;
            color: #888;
            margin: 8px 0 8px 0;
            padding: 0 16px;
            word-break: break-word;
            white-space: pre-line;
            width: 100%;
        }
    </style>
</head>
<body>
    <div class="chat-widget">
        <div class="chat-header">
          ${logoCode}
          <span style="font-weight:bold;font-size:18px;">${customization.botTitle || 'Chat Bot'}</span>
        </div>
        <div class="chat-messages">
          <!-- Messages would be rendered here by your JS -->
        </div>
        <div class="chat-input">
          <input type="text" placeholder="${customization.inputPlaceholder || 'Type your message...'}" style="flex-grow:1;" />
          <button>Send</button>
        </div>
        ${customization.disclaimerText ? `<div class="chat-disclaimer" style="text-align:${customization.disclaimerAlign || 'left'};">${customization.disclaimerText.replace(/\n/g, '<br>')}</div>` : ''}
    </div>
</body>
</html>
    `;
  };

  // --- Customizer Preview Test Handlers ---
  const handleTestIdle = () => {
    // Instantly show the idle message in the preview
    const timestamp = new Date();
    setMessages(prev => {
      const newMessages = [
        ...prev,
        {
          text: customization.idleMessage,
          isBot: true,
          timestamp
        }
      ];
      return newMessages;
    });
  };

  const handleTestClose = () => {
    // Instantly show the close message and close the chat in the preview
    const timestamp = new Date();
    setMessages(prev => {
      const newMessages = [
        ...prev,
        {
          text: customization.closeIdleMessage,
          isBot: true,
          timestamp
        }
      ];
      return newMessages;
    });
    setTimeout(() => setIsChatClosed(true), 500); // short delay for realism
  };

  const handleResetPreview = () => {
    // Reset the chat preview to initial state (empty messages, open chat)
    setMessages([]);
    setIsChatClosed(false);
  };

  // Get the open dialog state from BotCustomizerPanel
  const [openDialog, setOpenDialog] = useState(null);
  // Track if Agent Information modal should auto-open
  const [agentInfoModalOpen, setAgentInfoModalOpen] = useState(false);

  // Auto-open Agent Information modal if newAgent=true in query string
  useEffect(() => {
    if (newAgent) {
      setAgentInfoModalOpen(true);
    }
  }, [newAgent]);

  // Session management functions
  const endSession = async (reason = 'closed') => {
    const currentSessionId = sessionId;
    if (!currentSessionId) return;

    try {
      console.log('🔚 Ending internal session:', currentSessionId, 'reason:', reason);

      // Close WebSocket first if it exists
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        console.log('🔌 Closing WebSocket connection due to session end');
        // Set the intentional closure flag before closing
        intentionallyClosedRef.current = true;
        wsRef.current.close(1000, 'Session ended'); // Normal closure
        wsRef.current = null;
        setWs(null);
      }

      // End session using the public API endpoint
      await fetch(`${API_URL}/public/chat/end-session/${currentSessionId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason: reason, internal: true })
      });

      // Store the ended session ID for history loading on reopen (like working widget)
      localStorage.setItem('chatMainLastSessionId', currentSessionId);

      // Clear current session state but keep messages in React state
      setSessionId(null);
      console.log('✅ Internal session ended successfully, stored for history loading');

    } catch (error) {
      console.error('❌ Error ending internal session:', error);
    }
  };

  // Handle page unload - end session
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Use current sessionId from state or last stored session
      const currentSessionId = sessionId || localStorage.getItem('chatMainLastSessionId');
      if (currentSessionId) {
        console.log('🚪 Page unloading, ending internal session:', currentSessionId);
        // Use fetch with keepalive for reliable session cleanup
        fetch(`${API_URL}/public/chat/end-session/${currentSessionId}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ reason: 'closed', internal: true }),
          keepalive: true // Key for reliable cleanup on page unload
        }).catch(err => console.error('Failed to end internal session on unload:', err));
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [sessionId]);

  // Ref for BotCustomizerPanel
  const botCustomizerPanelRef = useRef();

  // Expose imperative methods for AgentDetail
  useImperativeHandle(ref, () => ({
    saveAgentCustomization: async () => {
      if (botCustomizerPanelRef.current && botCustomizerPanelRef.current.saveAgentCustomization) {
        return await botCustomizerPanelRef.current.saveAgentCustomization();
      }
    },
    resetCustomization: () => {
      if (botCustomizerPanelRef.current && botCustomizerPanelRef.current.resetCustomization) {
        botCustomizerPanelRef.current.resetCustomization();
      }
    }
  }));

  return (
    <Box sx={{ display: 'flex', }}>
      {/* System Alert at viewport level */}
      {alert && (
        <SystemAlert open={!!alert} severity={alert.severity} message={alert.message} onClose={() => setAlert(null)} />
      )}
      <Box sx={{ width: `${sidebarWidth}px`, position: 'relative', flexShrink: 0, borderRight: 1, borderColor: 'divider', zIndex: 2 }}>
        <BotCustomizerPanel
          ref={botCustomizerPanelRef}
          customization={customization}
          setCustomization={setCustomization}
          devicePreview={devicePreview}
          setDevicePreview={setDevicePreview}
          setExportDialogOpen={setExportDialogOpen}
          onTestIdle={handleTestIdle}
          onTestClose={handleTestClose}
          onResetPreview={handleResetPreview}
          openDialog={openDialog}
          setOpenDialog={setOpenDialog}
          agentId={newAgent ? null : agentId}
          tenantId={Cookies.getTenantId()}
          selectedInstructionId={selectedInstructionId}
          setSelectedInstructionId={setSelectedInstructionId}
          agentInfoModalOpen={agentInfoModalOpen}
          setAgentInfoModalOpen={setAgentInfoModalOpen}
          newAgent={newAgent}
          mode={newAgent ? "create" : "edit"}
          setAlert={setAlert}
          onCustomizationSaved={onCustomizationSaved}
          hasUnsavedChanges={hasUnsavedChanges}
          setHasUnsavedChanges={setHasUnsavedChanges}
          lastSavedCustomization={lastSavedCustomization}
          setLastSavedCustomization={setLastSavedCustomization}
          resetCustomization={resetCustomization}
          onInstructionsModalOpenChange={setIsInstructionsModalOpen}
          {...rest}
        />
        <DragHandle
          sidebarWidth={sidebarWidth}
          setSidebarWidth={setSidebarWidth}
        />
      </Box>

      <Box sx={{
        flexGrow: 1,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        transition: 'margin-left 0.3s ease-in-out',
        marginLeft: openDialog ? '400px' : '0',
        position: 'relative',
      }}>
        {/* <ChatWidget ... /> removed to prevent duplicate widgets */}
        {/* Refresh Chat button in white space, only on Customize Chatbot tab */}
        {!isInstructionsModalOpen && (
          <Box
            sx={{
              position: 'absolute',
              top: -10,
              right: -10,
              zIndex: 1200,
              pointerEvents: 'none',
              width: 'auto',
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            <Button
              variant="contained"
              color="primary"
              size="small"
              sx={{
                fontWeight: 600,
                borderRadius: 2,
                textTransform: 'none',
                boxShadow: 1,
                pointerEvents: 'auto',
              }}
              onClick={async () => {
                // Build new messages array with greeting if enabled
                let newMessages = [];
                if (customization.greetingEnabled !== false) {
                  newMessages.push({
                    text: customization.greetingText || 'Hi there, how can I help you today?',
                    isBot: true,
                    timestamp: new Date()
                  });
                }
                setMessages(newMessages);
                setInitialMessageSent(false);
                // Also reset starter prompts if present in customization
                if (customization.presetResponses && customization.presetResponses.length > 0) {
                  setCustomization(prev => ({
                    ...prev,
                    presetResponses: prev.presetResponses.map(pr => ({ ...pr, active: true }))
                  }));
                }
                // No need to touch the websocket/session!
              }}
            >
              Refresh Chat
            </Button>
          </Box>
        )}
        <ChatWidget
          key={chatWidgetKey}
          customization={customization}
          messages={messages}
          setMessages={setMessages}
          isChatClosed={isChatClosed}
          setIsChatClosed={async (closed) => {
            setUserToggledChat(true);
            if (closed) {
              // End the session when chat is closed (X button clicked)
              await endSession('closed');
            } else {
              // When reopening, messages persist in React state (like real widget)
              console.log('🔄 Reopening chat - messages preserved in React state');
            }
            setIsChatClosed(closed);
          }}
          ws={ws}
          setWs={setWs}
          isConnected={isConnected}
          setIsConnected={setIsConnected}
          devicePreview={devicePreview}
          currentBotMessageRef={currentBotMessageRef}
          soundEnabledRef={soundEnabledRef}
          isMutedRef={isMutedRef}
          isMuted={isMuted}
          setIsMuted={setIsMuted}
          sessionId={sessionId}
          onEndSession={endSession}
          tenantId={Cookies.getTenantId()}
          agentId={agentId}
          hasUnsavedChanges={hasUnsavedChanges}
          setHasUnsavedChanges={setHasUnsavedChanges}
          lastSavedCustomization={lastSavedCustomization}
          setLastSavedCustomization={setLastSavedCustomization}
          resetCustomization={resetCustomization}
          initialMessageSent={initialMessageSent}
          setInitialMessageSent={setInitialMessageSent}
          showCloseConfirm={showCloseConfirm}
          setShowCloseConfirm={setShowCloseConfirm}
          closeConfirmText={closeConfirmText}
          setCloseConfirmText={setCloseConfirmText}
          loading={loading}
          setLoading={setLoading}
          calcomWidget={calcomWidget}
          setCalcomWidget={setCalcomWidget}
        />
      </Box>

      <IdleDetectionManager
        enabled={customization.idleDetectionEnabled}
        idleTimeout={customization.idleTimeout}
        closeTimeout={customization.closeIdleTimeout}
        onIdle={handleIdleMessage}
        onClose={handleIdleClose}
        hasMessages={messages.length > 0}
        isChatClosed={isChatClosed}
        key={`idle-${customization.idleTimeout}-${customization.closeIdleTimeout}`}
      />

      {/* Export Code Dialog */}
      <Dialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Export Chat Widget Code
          <IconButton
            aria-label="close"
            onClick={() => setExportDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            Copy this code and paste it into your website to embed the chat widget.
          </Typography>
          <Box sx={{ position: 'relative' }}>
            <TextField
              multiline
              fullWidth
              rows={20}
              value={generateExportCode()}
              InputProps={{
                readOnly: true,
              }}
              sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}
            />
            <Tooltip title="Copy to clipboard">
              <IconButton
                onClick={handleCopyCode}
                sx={{ position: 'absolute', top: 8, right: 8 }}
              >
                <ContentCopyIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Copy Success Snackbar */}
      <Snackbar
        open={copySuccess}
        autoHideDuration={3000}
        onClose={() => setCopySuccess(false)}
        message="Code copied to clipboard!"
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
    </Box>
  );
});

export default ChatMain;
